import React, { useRef, useEffect } from 'react';
import './Map.css';

/**
 * Contexts
 */
import MapContext from './MapContext';
import { useGlobalVariablesContext } from '../../context/GlobalVariablesContext';

/**
 * OL
 */
import * as ol from 'ol';
import Attribution from 'ol/control/Attribution.js';
import { Zoom } from 'ol/control';
import { fromLonLat } from 'ol/proj';
import { transformExtent } from 'ol/proj';
import { DragPan } from 'ol/interaction';

function Map({ children }) {
    const mapElementRef = useRef();
    const { config, mapRef } = useGlobalVariablesContext(); // obtenemos el ref global

    useEffect(() => {
        if (!mapElementRef.current || !config?.zoom || !config?.center) return;

        // Configuración base de la vista
        const viewConfig = {
            zoom: config.zoom,
            center: fromLonLat(config.center),
            minZoom: config.minZoom || 0,
            maxZoom: config.maxZoom || 28,
            pixelRatio: 1,
        };

        // Si hay bbox definido, limitar el extent del mapa
        if (config.bbox && Array.isArray(config.bbox) && config.bbox.length === 4) {
            // El bbox viene en formato [minX, minY, maxX, maxY] en lon/lat (EPSG:4326)
            // Necesitamos transformarlo a la proyección del mapa (EPSG:3857)
            const bboxExtent = transformExtent(
                config.bbox,
                'EPSG:4326',
                'EPSG:3857'
            );
            
            viewConfig.extent = bboxExtent;
            viewConfig.constrainOnlyCenter = false; // Permite que todo el mapa esté dentro del extent
            
            // Opcional: Ajustar el centro si está fuera del bbox
            const center = fromLonLat(config.center);
            const [minX, minY, maxX, maxY] = bboxExtent;
            
            // Verificar si el centro está dentro del bbox
            if (center[0] < minX || center[0] > maxX || center[1] < minY || center[1] > maxY) {
                // Si el centro está fuera del bbox, usar el centro del bbox
                viewConfig.center = [
                    (minX + maxX) / 2,
                    (minY + maxY) / 2
                ];
            }
        }

        const options = {
            view: new ol.View(viewConfig),
            layers: [],
            controls: [],
            overlays: []
        };

        const mapObject = new ol.Map(options);
        mapObject.setTarget(mapElementRef.current);
        mapRef.current = mapObject; // ← guardamos en el contexto global

        // ATRIBUTOS
        const attributionControl = new Attribution({ collapsible: true });
        mapObject.addControl(attributionControl);

        const attributionButton = document.querySelector('.ol-attribution button');
        attributionButton?.addEventListener('click', () => {
            const attributionList = document.querySelector('.ol-attribution ul');
            attributionList?.classList.toggle('ol-collapsed');
        });

        // ZOOM
        const zoomControl = new Zoom({
            className: 'custom-zoom',
            zoomInClassName: 'custom-zoom-in rounded',
            zoomOutClassName: 'custom-zoom-out rounded'
        });
        mapObject.addControl(zoomControl);

        // DRAG PAN con botón central
        const dragPan = new DragPan({
            condition: (event) => event.originalEvent.button === 1,
        });
        mapObject.addInteraction(dragPan);

        // URL params: ?center=lon,lat&zoom=N to override initial view
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const centerParam = urlParams.get('center');
            if (centerParam) {
                const [lon, lat] = centerParam.split(',').map(Number);
                if (!isNaN(lon) && !isNaN(lat)) {
                    const zoomParam = urlParams.get('zoom');
                    mapObject.getView().setCenter(fromLonLat([lon, lat]));
                    mapObject.getView().setZoom(zoomParam ? parseInt(zoomParam) : 18);
                }
            }
        } catch (_) { /* ignore malformed params */ }

        return () => {
            mapObject.setTarget(undefined);
        };
    }, [config, mapElementRef.current]);

    return (
        <MapContext.Provider value={{ map: mapRef.current }}>
            <div ref={mapElementRef} className='ol-map'>
                {children}
            </div>
        </MapContext.Provider>
    );
}

export default Map;

